import { useState } from "react";
import { Code, ConnectError } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData, skipToken } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { formatDate } from "@yatayat/core/date";
import { formatNumber, formatPaisa } from "@yatayat/core/money";
import { FareService } from "@yatayat/gen/yatayat/fares/v1/fares_pb";
import { RoadClass } from "@yatayat/gen/yatayat/network/v1/network_pb";
import { VehicleClass } from "@yatayat/gen/yatayat/types/v1/types_pb";

import { HillsArt } from "~/components/HillsArt";
import { PlaceSelect, type PlaceOption } from "~/components/PlaceSelect";
import { Loading, QueryError } from "~/components/QueryState";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { useSortedPlaces } from "~/lib/places";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/fare-check")({
  component: FareCheck,
});

const classes: { value: VehicleClass; label: MessageKey }[] = [
  { value: VehicleClass.ORDINARY, label: "classOrdinary" },
  { value: VehicleClass.DELUXE, label: "classDeluxe" },
  { value: VehicleClass.SUPER_DELUXE, label: "classSuperDeluxe" },
];

const roadClassLabels: Partial<Record<RoadClass, MessageKey>> = {
  [RoadClass.TERAI]: "roadTerai",
  [RoadClass.HILL]: "roadHill",
  [RoadClass.KTM_POKHARA]: "roadKtmPokhara",
};

function FareCheck() {
  const { t, lang } = useI18n();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [vehicleClass, setVehicleClass] = useState(VehicleClass.ORDINARY);
  const [electric, setElectric] = useState(false);

  const placeList = useSortedPlaces();
  const sortedPlaces = placeList.places;
  const placeholder = placeList.isPending ? t("loading") : t("choosePlace");

  const ready = from !== "" && to !== "" && from !== to;
  const quote = useQuery(
    FareService.method.quoteFare,
    ready ? { fromCode: from, toCode: to, vehicleClass, electric } : skipToken,
    // Keep the last fare on screen while a changed class or route is fetched.
    { placeholderData: keepPreviousData },
  );

  const km = (n: number) => formatNumber(n, lang, { maxFraction: 1 });

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <HillsArt className="mb-4 h-16 sm:h-20" />
        <h1 className={ui.h1}>{t("fareCheck")}</h1>
        <p className={ui.lead}>{t("fareCheckIntro")}</p>
      </div>

      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        <div className="grid gap-4 sm:grid-cols-2">
          <PlaceSelect label={t("from")} value={from} onChange={setFrom} places={sortedPlaces} placeholder={placeholder} />
          <PlaceSelect label={t("to")} value={to} onChange={setTo} places={sortedPlaces} placeholder={placeholder} />
        </div>

        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t("busClass")}</legend>
          <div className="flex flex-wrap gap-2">
            {classes.map((c) => (
              <label
                key={c.value}
                className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                  vehicleClass === c.value
                    ? "border-ink bg-ink text-white dark:border-ink-dark dark:bg-ink-dark dark:text-slate-950"
                    : "border-slate-300 dark:border-slate-700"
                }`}
              >
                <input
                  type="radio"
                  name="vehicleClass"
                  className="sr-only"
                  checked={vehicleClass === c.value}
                  onChange={() => setVehicleClass(c.value)}
                />
                {t(c.label)}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={electric} onChange={(e) => setElectric(e.target.checked)} className="h-4 w-4" />
          {t("electricBus")}
        </label>
      </form>

      {placeList.isError && <QueryError onRetry={() => void placeList.refetch()} busy={placeList.isFetching} />}

      {from !== "" && from === to && <p className={ui.error}>{t("samePlace")}</p>}

      {quote.isError && <p className={ui.error}>{quoteError(quote.error, t)}</p>}

      {ready && quote.isPending && <Loading />}

      {ready && quote.data && (
        <section aria-live="polite" className="rounded-xl border border-slate-200 p-5 dark:border-slate-800">
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("officialFare")}</p>
          <p className="mt-1 text-4xl font-semibold text-ink dark:text-ink-dark">
            {formatPaisa(quote.data.officialFare?.paisa ?? 0n, lang)}
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {t("distanceKm", { km: km(quote.data.distanceKm) })} ·{" "}
            {t(classes.find((c) => c.value === quote.data.vehicleClass)?.label ?? "classOrdinary")}
            {quote.data.electric ? ` · ${t("electricBus")}` : ""}
          </p>
          {quote.data.fareVersion && (
            <p className="mt-3 text-sm">
              {t("fareTable", {
                label: quote.data.fareVersion.label,
                date: formatDate(Number(quote.data.fareVersion.effectiveFromMs), lang),
              })}
            </p>
          )}

          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-slate-600 dark:text-slate-400">{t("howWorkedOut")}</summary>
            <table className="mt-2 w-full text-left">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 font-normal">{t("stretch")}</th>
                  <th className="py-1 font-normal">{t("road")}</th>
                  <th className="py-1 text-right font-normal">{t("km")}</th>
                  <th className="py-1 text-right font-normal">{t("perKm")}</th>
                </tr>
              </thead>
              <tbody>
                {quote.data.lines.map((l) => (
                  <tr key={`${l.fromCode}-${l.toCode}`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1">
                      {placeName(sortedPlaces, l.fromCode, lang)} → {placeName(sortedPlaces, l.toCode, lang)}
                    </td>
                    <td className="py-1">{t(roadClassLabels[l.roadClass] ?? "roadHill")}</td>
                    <td className="py-1 text-right">{km(l.distanceKm)}</td>
                    <td className="py-1 text-right">{formatPaisa(l.ratePerKm?.paisa ?? 0n, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-slate-500">{t("howWorkedOutNote")}</p>
          </details>
        </section>
      )}
    </div>
  );
}

function placeName(places: PlaceOption[], code: string, lang: "ne" | "en") {
  return places.find((p) => p.code === code)?.name?.[lang] ?? code;
}

function quoteError(err: unknown, t: (key: MessageKey) => string) {
  switch (ConnectError.from(err).code) {
    case Code.NotFound:
      return t("noRoute");
    case Code.FailedPrecondition:
      return t("noFareTable");
    default:
      return t("somethingWrong");
  }
}
